import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const BG = "https://images.unsplash.com/photo-1497366754035-f200968a6e72?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBvZmZpY2V8ZW58MHx8fHwxNzg1MTU0NjEyfDA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login, verifyLoginOtp, error } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  // OTP step state
  const [otpStep, setOtpStep] = useState(null); // { login_token, demo_hint }
  const [otp, setOtp] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const res = await login(email, password);
    setBusy(false);
    if (res.pendingOtp) {
      setOtpStep({ login_token: res.login_token, demo_hint: res.demo_hint });
      return;
    }
    if (res.user) nav("/dashboard");
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    const res = await verifyLoginOtp(otpStep.login_token, otp);
    setBusy(false);
    if (res.success) nav("/dashboard");
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-zinc-950">
      {/* Left panel */}
      <div className="relative hidden lg:block">
        <img src={BG} alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-zinc-950/70" />
        <div className="relative h-full p-12 flex flex-col justify-between text-zinc-100">
          <div>
            <img src="/param-logo.png" alt="Param Skills Training (I) Pvt. Ltd."
              className="h-24 w-auto object-contain brightness-0 invert -ml-3" />
          </div>
          <div>
            <h1 className="font-heading text-5xl leading-[0.95] tracking-tight">
              Project-wise<br />Finance<br />Operating System.
            </h1>
            <p className="mt-6 text-zinc-300 max-w-md text-sm leading-relaxed">
              Enter once. Approve once. Pay once. Automatically account centre-wise.
              A single source of truth for every centre, every rupee.
            </p>
          </div>
          <div className="text-xs text-zinc-500">© 2026 Param Skills. All rights reserved.</div>
        </div>
      </div>

      {/* Right panel - Form */}
      <div className="bg-white flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">
          {!otpStep ? (
            <>
              <div className="text-xs tracking-[0.3em] uppercase text-zinc-500">Secure Access</div>
              <h2 className="font-heading text-3xl mt-2 tracking-tight">Sign in to continue</h2>
              <p className="text-sm text-zinc-500 mt-1">Use your organisation credentials.</p>

              <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
                <div>
                  <Label htmlFor="email" className="text-xs uppercase tracking-widest">Email</Label>
                  <Input id="email" type="email" required value={email}
                    onChange={(e) => setEmail(e.target.value)} data-testid="login-email"
                    className="mt-2 rounded-sm border-zinc-300 h-11" placeholder="you@paramskills.in" />
                </div>
                <div>
                  <Label htmlFor="password" className="text-xs uppercase tracking-widest">Password</Label>
                  <Input id="password" type="password" required value={password}
                    onChange={(e) => setPassword(e.target.value)} data-testid="login-password"
                    className="mt-2 rounded-sm border-zinc-300 h-11" />
                </div>
                {error && (
                  <div className="text-sm text-rose-700 border-l-2 border-rose-600 pl-3 py-1 bg-rose-50" data-testid="login-error">
                    {error}
                  </div>
                )}
                <Button type="submit" disabled={busy} data-testid="login-submit"
                  className="w-full h-11 rounded-sm bg-zinc-900 hover:bg-zinc-800 text-white">
                  {busy ? "Signing in…" : "Continue →"}
                </Button>
                <div className="text-right">
                  <a href="/forgot-password" data-testid="forgot-link"
                    className="text-xs uppercase tracking-widest text-zinc-500 hover:text-black">
                    Forgot password?
                  </a>
                </div>
              </form>
            </>
          ) : (
            <>
              <div className="text-xs tracking-[0.3em] uppercase text-zinc-500">Two-Step Verification</div>
              <h2 className="font-heading text-3xl mt-2 tracking-tight">Enter your code</h2>
              <p className="text-sm text-zinc-500 mt-1">
                We just sent a 6-digit code to <b>{email}</b>. It expires in 10 minutes.
              </p>
              {otpStep.demo_hint && (
                <div className="mt-4 text-xs bg-amber-50 border border-amber-300 p-3 rounded-sm" data-testid="otp-demo-hint">
                  Demo mode (no live email domain configured). Code: <b>{otpStep.demo_hint}</b>
                </div>
              )}
              <form onSubmit={verify} className="mt-6 space-y-5" data-testid="otp-form">
                <div>
                  <Label className="text-xs uppercase tracking-widest">One-Time Code</Label>
                  <Input value={otp} onChange={(e) => setOtp(e.target.value)}
                    inputMode="numeric" maxLength={8} required autoFocus data-testid="otp-code"
                    className="mt-2 rounded-sm border-zinc-300 h-11 tracking-[0.3em] text-center text-lg" />
                </div>
                {error && (
                  <div className="text-sm text-rose-700 border-l-2 border-rose-600 pl-3 py-1 bg-rose-50" data-testid="otp-error">
                    {error}
                  </div>
                )}
                <Button type="submit" disabled={busy} data-testid="otp-verify"
                  className="w-full h-11 rounded-sm bg-zinc-900 hover:bg-zinc-800 text-white">
                  {busy ? "Verifying…" : "Verify & Sign in →"}
                </Button>
                <button type="button" onClick={() => { setOtpStep(null); setOtp(""); }}
                  data-testid="otp-back" className="text-xs uppercase tracking-widest text-zinc-500 hover:text-black">
                  ← Back to sign in
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
